import { apiClient } from './api'

export interface GatewayGps {
  lat: number
  lng: number
  alt: number
}

export interface Gateway {
  id: string
  macAddress: string
  name: string | null
  status: 'active' | 'suspended'
  online: boolean
  lastSeenAt: string | null
  batteryPercent: number | null
  gatewayVersion: string | null
  gps: GatewayGps | null
  yardId: string | null
  yardName: string | null
  assignmentMode: 'auto' | 'manual' | null
  unassigned: boolean
  autoRegistered?: boolean
  deviceCount: number
  activeDeviceCount: number
  transmitInterval: number | null
  measureInterval: number | null
  apiKeyPrefix: string | null
  createdAt: string
}

export interface GatewaySensor {
  macAddress: string
  lastSeenAt: string | null
  lastTrend: 'bad' | 'normal' | 'good' | 'very good' | null
  lastBatteryPercent: number | null
  lastSignal: number | null
  sensorVersion: string | null
  online: boolean
}

export interface TelemetryLogEntry {
  packetId: string
  receivedAt: string
  sensorCount: number
  batteryPercent: number
  gatewayVersion: string | null
  gps: GatewayGps | null
}

export interface GatewayAlert {
  id: string
  type: 'warning' | 'error' | 'info'
  message: string
  date: string
  resolved: boolean
}

export interface GatewayDetail extends Gateway {
  sensors: GatewaySensor[]
  recentPackets: TelemetryLogEntry[]
  recentAlerts: GatewayAlert[]
}

export interface DeviceRow {
  macAddress: string
  name: string | null
  gatewayMacAddress: string
  gatewayName: string | null
  gatewayId: string | null
  yardId: string | null
  yardName: string | null
  unassigned: boolean
  lastTrend: string | null
  lastBatteryPercent: number | null
  lastSignal: number | null
  sensorVersion: string | null
  lastSeenAt: string | null
  online: boolean
}

export interface PacketDetail {
  packetId: string
  receivedAt: string
  sensorCount: number
  received: unknown
}

export interface DeviceDetail extends DeviceRow {
  firstSeenAt: string
  readingCount: number
}

export interface DeviceReadingPoint {
  timestamp: string
  batteryPercent: number
  signal: number
  trend: 'bad' | 'normal' | 'good' | 'very good'
}

export interface DeviceLogEntry {
  id: string
  packetId: string
  timestamp: string
  receivedAt: string
  trend: 'bad' | 'normal' | 'good' | 'very good'
  batteryPercent: number
  signal: number
  sensorVersion: string | null
  alert: { code: string; message: string } | null
}

export interface Paginated<T> {
  data: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
}

export async function getGateways(): Promise<Gateway[]> {
  const res = await apiClient.get('/gateways')
  return res.data
}

export async function getDevices(): Promise<DeviceRow[]> {
  const res = await apiClient.get('/gateways/devices')
  return res.data
}

export async function getDevice(macAddress: string): Promise<DeviceDetail> {
  const res = await apiClient.get(`/gateways/devices/${encodeURIComponent(macAddress)}`)
  return res.data
}

export async function getDeviceReadings(macAddress: string, hours = 168): Promise<DeviceReadingPoint[]> {
  const res = await apiClient.get(`/gateways/devices/${encodeURIComponent(macAddress)}/readings`, { params: { hours } })
  return res.data
}

export async function getDeviceLogs(macAddress: string, page = 1, limit = 50): Promise<Paginated<DeviceLogEntry>> {
  const res = await apiClient.get(`/gateways/devices/${encodeURIComponent(macAddress)}/logs`, { params: { page, limit } })
  return res.data
}

export async function updateDevice(macAddress: string, payload: { name: string }): Promise<{ macAddress: string; name: string | null }> {
  const res = await apiClient.patch(`/gateways/devices/${encodeURIComponent(macAddress)}`, payload)
  return res.data
}

export async function getPacket(packetId: string): Promise<PacketDetail> {
  const res = await apiClient.get(`/gateways/packet/${packetId}`)
  return res.data
}

export async function getGateway(id: string): Promise<GatewayDetail> {
  const res = await apiClient.get(`/gateways/${id}`)
  return res.data
}

export async function getGatewayLogs(
  id: string,
  page = 1,
  limit = 50
): Promise<{ data: TelemetryLogEntry[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const res = await apiClient.get(`/gateways/${id}/logs`, { params: { page, limit } })
  return res.data
}

export async function provisionGateway(payload: {
  macAddress: string
  name?: string
  transmitInterval?: number
  measureInterval?: number
}): Promise<{ id: string; macAddress: string; name: string | null; apiKey: string; apiKeyPrefix: string }> {
  const res = await apiClient.post('/gateways', payload)
  return res.data
}

export async function assignGateway(id: string, yardId: string): Promise<void> {
  await apiClient.post(`/gateways/${id}/assign`, { yardId })
}

export async function unassignGateway(id: string): Promise<void> {
  await apiClient.post(`/gateways/${id}/unassign`)
}

export async function updateGateway(
  id: string,
  payload: Partial<{ name: string; status: 'active' | 'suspended'; transmitInterval: number; measureInterval: number }>
): Promise<void> {
  await apiClient.patch(`/gateways/${id}`, payload)
}

export async function deleteGateway(id: string): Promise<void> {
  await apiClient.delete(`/gateways/${id}`)
}
